import {useCallback, useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {List} from '@tickchak/react-share/app';
import {getSupportVideos} from '../../../redux/management/managementActions';
import {TSupportVideos} from '../../../redux/management/management.type';
import Loader from '../../../component/loader/Loader';
import SupportVideosHeader from './SupportVideosHeader';
import useRouteInfo from '../hooks/useRouteInfo';
import * as css from './supportVideos.css';
import useFilteredVideos from './hooks/useFilteredVideos';
import {ManagementState, SupportVideosListProps} from './SupportVideos.type';
import VideoCard from '../VideoCard/VideoCard';
import { useTranslation } from 'react-i18next';
import NoResultsIcon from '../../../assets/icons/noResults';

const LIMIT = 20;

interface SupportVideosListInternalProps extends SupportVideosListProps {
    onOpenRating: () => void;
}

function SupportVideosList({onOpenVideo, onOpenForm, expandedVideoId, onExpandedVideoIdChange, onOpenRating}: SupportVideosListInternalProps) {
    const {t} = useTranslation('layout');
    const containerRef = useRef<HTMLDivElement>(null);
    const {routeType, id, pageType} = useRouteInfo();

    const videos = useSelector((state: {management: ManagementState}) => state.management.videos);
    const videosXHR = useSelector((state: {management: ManagementState}) => state.management.videosXHR);

    const [isShowingAll, setIsShowingAll] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [isEndOfResults, setIsEndOfResults] = useState(false);

    const fetchVideos = useCallback(async (offset: number, search: string, showAll: boolean) => {
        const isEnd = await getSupportVideos(pageType, routeType, id, search, !showAll, LIMIT, offset);
        setIsEndOfResults(isEnd);
    }, [pageType, routeType, id]);

    const refreshVideos = useCallback((search = '', showAll = false) => {
        if (id || pageType === 'login' || pageType === 'signup') {
            setSearchValue(search);
            setIsShowingAll(showAll);
            fetchVideos(0, search, showAll);
        }
    }, [id, pageType, fetchVideos]);

    useEffect(() => {
        refreshVideos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageType, routeType, id]);

    const loadMore = useCallback(() => {
        if (isEndOfResults) return;
        fetchVideos(videos.length, searchValue, isShowingAll);
    }, [isEndOfResults, videos.length, searchValue, isShowingAll, fetchVideos]);

    const {currentPageVideos, otherVideos} = useFilteredVideos(videos, pageType, isShowingAll);

    const onClickOutside = useCallback((e: React.MouseEvent) => {
        if (e.target === e.currentTarget) onExpandedVideoIdChange(-1);
    }, [onExpandedVideoIdChange]);

    const renderVideoCard = (video: TSupportVideos) => (
        <VideoCard 
            key={video.id}
            video={video}
            editVideo={(videoId: number) => onOpenForm(videoId, pageType)}
            onOpenVideo={onOpenVideo}
            currentExpended={expandedVideoId}
            setCurrentExpended={onExpandedVideoIdChange}
        />
    );
    
    return (
        <>
            <SupportVideosHeader 
                isShowingAll={isShowingAll}
                searchValue={searchValue}
                onRefresh={refreshVideos}
                onOpenForm={onOpenForm}
                onOpenRating={onOpenRating}
                pageType={pageType}
            />
            <List.Items>                       
                <Loader XHR={videosXHR} onScrollEnd={loadMore} containerRef={containerRef} height={'100%'}>
                    <css.videos onClick={onClickOutside}>
                        {currentPageVideos.map(renderVideoCard)}
                        {!!otherVideos.length && <css.moreResults>{t('moreResults')}</css.moreResults>}
                        {otherVideos.map(renderVideoCard)}
                        {!currentPageVideos.length && !otherVideos.length && <css.noResults>
                            <NoResultsIcon />
                            <span>{t('noResults')}</span>
                        </css.noResults>}
                    </css.videos>
                </Loader>
            </List.Items>
        </>
    );
}

export default SupportVideosList;
